import { apiUrls } from "../../../actprep-ui/utils/urls";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { useGet } from "../../../actprep-ui/hooks/api";
import {
  formatPhoneNumber,
  fullName,
  stripParenthesizedWords,
} from "../../../actprep-ui/utils/formatting";
import { Chip, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { Edit } from "@mui/icons-material";
import useDisclosure from "../../../actprep-ui/hooks/ui/useDisclosure";
import StudentStartScoreEditor from "../../../components/StudentStartScoreEditor";

export default function StudentEnrollmentInformation({
  studentProfile,
  onSubmit,
}) {
  const { data } = useGet(apiUrls.admin.tickets(), {
    params: {
      search_params: {
        student_id: studentProfile?.uuid,
      },
      keep_related: true,
      values: ["seat_type", "program_type", "cohort"],
      order_by: ["-cohort__start_date"],
    },
    enabled: !!studentProfile,
  });
  const startScoreEditorDisc = useDisclosure();
  const tickets = data?.data || [];
  return (
    <>
      <Grid container spacing={1}>
        <Grid size={12}>
          <Typography variant="subtitle1">About (from parent)</Typography>
          <Typography variant="body2" sx={{ ml: ".5rem" }}>
            {studentProfile.personality || "N/A"}
          </Typography>
        </Grid>
        <Grid size={12}>
          <Typography variant="subtitle1">Address</Typography>
          <Typography variant="body2" sx={{ ml: ".5rem" }}>
            {studentProfile.mailing_address || "N/A"}
          </Typography>
          {studentProfile.mailing_address_line_2 && (
            <Typography variant="body2" sx={{ ml: ".5rem" }}>
              {studentProfile.mailing_address_line_2}
            </Typography>
          )}
          {studentProfile.mailing_address && (
            <Typography variant="body2" sx={{ ml: ".5rem" }}>
              {studentProfile.mailing_address_city},{" "}
              {studentProfile.mailing_address_state}{" "}
              {studentProfile.mailing_address_zip}
            </Typography>
          )}
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <Typography variant="subtitle1" mb={1}>
            Starting scores{" "}
            <Chip
              label="Edit"
              icon={<Edit />}
              size="small"
              {...startScoreEditorDisc.getButtonProps()}
            />
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>English</TableCell>
                  <TableCell>{studentProfile.start_english}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Math</TableCell>
                  <TableCell>{studentProfile.start_math}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Reading</TableCell>
                  <TableCell>{studentProfile.start_reading}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Science</TableCell>
                  <TableCell>{studentProfile.start_science}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Composite</TableCell>
                  <TableCell>{studentProfile.start_composite}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <Typography variant="subtitle1">Additional Details</Typography>
          <Grid size={12} container>
            <List disablePadding>
              <ListItem>
                <ListItemText
                  primary="Parent"
                  secondary={`${fullName(
                    studentProfile.parent?.user
                  )} - ${formatPhoneNumber(studentProfile.parent?.phone)}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="School"
                  secondary={studentProfile.school?.name}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Books Read"
                  secondary={studentProfile.fiction_novels_read}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="AP/IB Classes Taken"
                  secondary={studentProfile.num_ap_or_ib}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Commitment Level"
                  secondary={studentProfile.commitment_level}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Gold Award Path"
                  secondary={
                    studentProfile.gold_award_path_choice
                      ? studentProfile.gold_award_path_choice
                          .charAt(0)
                          .toUpperCase() +
                        studentProfile.gold_award_path_choice.slice(1)
                      : studentProfile.is_leadership_team
                        ? "Yes"
                        : "No"
                  }
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
        <Grid size={12}>
          <Typography variant="subtitle1">Tickets</Typography>
          <List disablePadding>
            {tickets.map((ticket, i) => (
              <ListItem key={i}>
                <ListItemText
                  primary={stripParenthesizedWords(ticket.program_type)}
                  secondary={`${stripParenthesizedWords(ticket.seat_type)} - ${
                    ticket.cohort?.event_name
                  }`}
                />
              </ListItem>
            ))}
          </List>
        </Grid>
      </Grid>
      <Dialog {...startScoreEditorDisc.getDisclosureProps()}>
        <DialogTitle>
          Editing {fullName(studentProfile)}
          {"'"}s Start Scores
        </DialogTitle>
        <DialogContent>
          <StudentStartScoreEditor
            studentId={studentProfile.uuid}
            onClose={startScoreEditorDisc.onClose}
            onSubmit={(newStudent) => {
              onSubmit({
                ...studentProfile,
                start_scores_image_uploaded: !!newStudent.start_scores_image,
              });
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
